import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  getFirebaseAuthUserFromLocalStorage,
  setFirebaseAuthUserInLocalStorage,
  getFirebaseAuthIdTokenFromLocalStorage,
  setFirebaseAuthIdTokenInLocalStorage,
} from "@/helpers/utils/local-storage";
import {
  type User,
  type IdTokenResult,
  getIdTokenResult,
  type ParsedToken,
} from "firebase/auth";
import { firebaseAuth } from "@/helpers/firebase/firebase";
import {
  changeUserEmail,
  hasPasswordAuth as checkHasPasswordAuth,
} from "@/helpers/firebase/AuthService";
import type { ChangeEmailResult } from "@/types/auth";
import type { AuthError } from "firebase/auth";

/**
 * Auth store managing Firebase authentication state
 * Uses Pinia with Composition API and TypeScript
 */
export const useAuthStore = defineStore("auth", () => {
  /* State */
  const loadingGoogleAuth = ref<boolean>(false);
  const loadingAppleAuth = ref<boolean>(false);
  const firebaseAuthUser = ref<User | null>(
    getFirebaseAuthUserFromLocalStorage(),
  );
  const firebaseAuthIdToken = ref<IdTokenResult | null>(
    getFirebaseAuthIdTokenFromLocalStorage(),
  );

  /* Computed / Getters */
  const loadingAuth = computed(
    () => loadingGoogleAuth.value || loadingAppleAuth.value,
  );
  const authUuid = computed<string | undefined>(
    () => firebaseAuthUser.value?.uid,
  );
  const authIdTokenClaims = computed<undefined | ParsedToken>(
    () => firebaseAuthIdToken.value?.claims,
  );

  /* Actions / Mutations */
  function cancelAuth(): void {
    loadingGoogleAuth.value = false;
    loadingAppleAuth.value = false;
  }

  function setGoogleAuthLoading(loading: boolean): void {
    loadingGoogleAuth.value = loading;
  }

  function setAppleAuthLoading(loading: boolean): void {
    loadingAppleAuth.value = loading;
  }

  function setAuthUser(user: User | null): void {
    firebaseAuthUser.value = user;
    setFirebaseAuthUserInLocalStorage(user);
  }

  function setAuthIdToken(token: IdTokenResult | null): void {
    firebaseAuthIdToken.value = token;
    setFirebaseAuthIdTokenInLocalStorage(token);
  }

  function reset(): void {
    firebaseAuthUser.value = null;
    firebaseAuthIdToken.value = null;
    setFirebaseAuthUserInLocalStorage(null);
    setFirebaseAuthIdTokenInLocalStorage(null);
  }

  async function refreshAuthIdToken() {
    if (firebaseAuthUser.value) {
      await firebaseAuthUser.value.reload();
      const token = await getIdTokenResult(firebaseAuthUser.value, true);
      setAuthIdToken(token);
    }
  }

  async function logout() {
    await firebaseAuth.signOut();
  }

  async function updateEmail(
    newEmail: string,
    password?: string,
  ): Promise<ChangeEmailResult> {
    try {
      await changeUserEmail(newEmail, password);
      await refreshAuthIdToken();
      return { success: true };
    } catch (error) {
      const authError = error as AuthError;
      if (authError.code === "auth/requires-recent-login") {
        return { success: false, requiresReauth: true };
      }
      return { success: false, error };
    }
  }

  function hasPasswordAuth() {
    return checkHasPasswordAuth();
  }

  /* Exposed store */
  return {
    // State
    loadingGoogleAuth,
    loadingAppleAuth,
    firebaseAuthUser,
    firebaseAuthIdToken,

    // Computed
    loadingAuth,
    authUuid,
    authIdTokenClaims,

    // Actions
    cancelAuth,
    setGoogleAuthLoading,
    setAppleAuthLoading,
    setAuthUser,
    setAuthIdToken,
    reset,
    logout,
    refreshAuthIdToken,
    updateEmail,
    hasPasswordAuth,
  };
});
